import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import onsiteproLogo from "../../assets/logo2 2.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";
const API_PATH = "/api/sf-004-g-plant-issue-form";
const ROW_COUNT = 10;

// Printed verbatim under the plant table on the paper form.
const DECLARATION = [
  "I, the above, named recipient operative understand that I am responsible for the safe use and deployment of the equipment issued to me, I will ensure that the equipment is used only by competent operatives while under my supervision and within the scope of prescribed operating method.",
  "I agree that the issued equipment will only be used for the purposes of which it has been designed.",
  "I agree that if the need arises for the plant to be handed over to another member of staff, that I take responsibility for an SF-004-G Plant Issue Form to be completed and handed in to office.",
];

const EMPTY_ROW = { plantTtslIdNo: "", descriptionOfPlant: "", comments: "", retestDate: "" };

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function parseStoredDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const d = new Date(parseInt(dmy[3], 10), parseInt(dmy[2], 10) - 1, parseInt(dmy[1], 10));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const d = new Date(parseInt(ymd[1], 10), parseInt(ymd[2], 10) - 1, parseInt(ymd[3], 10));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateToYmd(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalizeImageSource(value, fallbackMime = "image/png") {
  if (!value) return "";
  let src = value;
  if (Array.isArray(src)) src = src[0];
  if (src == null) return "";
  if (typeof src !== "string") src = String(src);
  src = src.trim();
  if (!src) return "";
  if (src.startsWith("data:image")) return src;
  if (/^(https?:)?\/\//i.test(src)) return src;
  const compact = src.replace(/\s+/g, "").replace(/,+$/, "");
  if (!compact) return "";
  return `data:${fallbackMime};base64,${compact}`;
}

function getPdfImageType(imageSrc, fallbackType = "PNG") {
  if (!imageSrc || typeof imageSrc !== "string") return fallbackType;
  const m = imageSrc.match(/^data:image\/([a-zA-Z0-9+.-]+);base64,/i);
  const subtype = m?.[1]?.toLowerCase();
  if (subtype === "png") return "PNG";
  if (subtype === "jpg" || subtype === "jpeg") return "JPEG";
  if (subtype === "webp") return "WEBP";
  return fallbackType;
}

// Records imported from the app may carry fewer than ROW_COUNT rows — pad so the
// grid and the PDF always match the ten rows printed on the paper form.
function ensureRows(value) {
  const rows = Array.isArray(value)
    ? value.map((r) => ({
        plantTtslIdNo: String(r?.plantTtslIdNo || ""),
        descriptionOfPlant: String(r?.descriptionOfPlant || ""),
        comments: String(r?.comments || ""),
        retestDate: String(r?.retestDate || ""),
      }))
    : [];
  while (rows.length < ROW_COUNT) rows.push({ ...EMPTY_ROW });
  return rows;
}

const GRID_LABEL = {
  border: "1px solid #cbd5e1",
  fontSize: 12,
  padding: "8px 10px",
  fontWeight: 700,
  color: THEME,
  background: "#f8f1f4",
  verticalAlign: "middle",
};
const GRID_FIELD = {
  border: "1px solid #cbd5e1",
  fontSize: 12,
  padding: "6px 8px",
  background: "#fff",
  verticalAlign: "middle",
};
const SECTION_BAR = {
  background: THEME,
  color: "#fff",
  fontWeight: 700,
  fontSize: 13,
  padding: "8px 10px",
};

function renderSignatureBox(value) {
  return (
    <div
      style={{
        border: "1px dashed #8b1634",
        borderRadius: 8,
        background: "#fbeef2",
        minHeight: 56,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 4,
      }}
    >
      {value ? (
        <img
          src={normalizeImageSource(value, "image/png")}
          alt="Signature"
          style={{ maxWidth: "100%", maxHeight: 48, objectFit: "contain" }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
      ) : (
        <span style={{ color: "#999", fontSize: 11 }}>No signature</span>
      )}
    </div>
  );
}

export default function Sf004GPlantIssueFormPage() {
  const canEditDelete = canEditDeleteRecords();
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);

  useEffect(() => {
    fetchRecords();
    fetchAppUsers();
  }, []);

  async function fetchRecords() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + API_PATH, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setRecords(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const filtered = records.filter((r) => {
    const fields = [r.operativeName, r.projectLocation, r.receivedByOperativeName]
      .map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((f) => f.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const getUserName = (record) => {
    const uid = record?.userId;
    const u = appUsers.find((x) => x._id === uid);
    if (u) {
      const full = `${u.firstName || ""} ${u.lastName || ""}`.trim();
      return full || uid || "—";
    }
    return addedByName(record) || uid || "—";
  };

  const handleEdit = (r) => {
    setEditForm({ ...r, plantRows: ensureRows(r?.plantRows), formUuid: ensureFormUuid(r?.formUuid) });
    setShowEditModal(true);
  };

  const updateRow = (index, key, value) =>
    setEditForm((prev) => {
      const rows = ensureRows(prev?.plantRows);
      rows[index] = { ...rows[index], [key]: value };
      return { ...prev, plantRows: rows };
    });

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + API_PATH + "/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          operativeName: editForm.operativeName,
          projectLocation: editForm.projectLocation,
          dateOfIssue: editForm.dateOfIssue,
          plantRows: ensureRows(editForm.plantRows),
          receivedByOperativeName: editForm.receivedByOperativeName,
          receivedByDate: editForm.receivedByDate,
          formUuid: ensureFormUuid(editForm.formUuid),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchRecords();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("Record updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) {
      toast.error("Invalid record id");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + API_PATH + "/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecords(records.filter((f) => f._id !== deleteId));
        toast.success("Record deleted!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const generatePDF = async (record, options = {}) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const primaryColor = [139, 22, 52];
    const headerHeight = 30;
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, headerHeight, "F");

    await new Promise((resolve) => {
      const logoImg = new Image();
      logoImg.crossOrigin = "anonymous";
      logoImg.onload = function onLoad() {
        try {
          const logoWidth = 70;
          const logoHeight = logoWidth * (logoImg.height / logoImg.width);
          const canvas = document.createElement("canvas");
          const scale = 2.5;
          canvas.width = logoImg.width * scale;
          canvas.height = logoImg.height * scale;
          const ctx = canvas.getContext("2d");
          ctx.scale(scale, scale);
          ctx.drawImage(logoImg, 0, 0);
          doc.addImage(canvas.toDataURL("image/png", 1.0), "PNG", 10, (headerHeight - logoHeight) / 2, logoWidth, logoHeight, "", "NONE");
        } catch {}
        resolve();
      };
      logoImg.onerror = () => resolve();
      logoImg.src = onsiteproLogo;
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text("SF-004-G Plant Issue Form", 90, 15);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", 90, 20.5);

    const innerW = pageWidth - 20;
    const labelCell = (t) => ({ content: t, styles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold" } });
    const val = (t) => ({ content: t || "", styles: { fontStyle: "bold" } });
    const common = {
      styles: { font: "helvetica", fontSize: 9, cellPadding: 2.5, overflow: "linebreak", valign: "middle", lineColor: [203, 213, 225], lineWidth: 0.2 },
      bodyStyles: { fontStyle: "bold" },
      theme: "grid",
      tableWidth: innerW,
      margin: { left: 10, right: 10 },
    };

    autoTable(doc, {
      ...common,
      startY: headerHeight + 8,
      body: [
        [labelCell("Operative Name:"), val(record.operativeName)],
        [labelCell("Project/Location:"), val(record.projectLocation)],
        [labelCell("Date of Issue:"), val(toDdMmYyyy(record.dateOfIssue))],
      ],
      columnStyles: { 0: { cellWidth: 45 } },
    });

    const rows = ensureRows(record.plantRows);
    autoTable(doc, {
      ...common,
      startY: doc.lastAutoTable.finalY + 3,
      head: [["Plant TTSL ID No.", "Description of Plant", "Comments:", "Re-test Date"]],
      headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold" },
      body: rows.map((r) => [
        r.plantTtslIdNo || "",
        r.descriptionOfPlant || "",
        r.comments || "",
        toDdMmYyyy(r.retestDate) || "",
      ]),
      columnStyles: { 0: { cellWidth: 34 }, 3: { cellWidth: 30 } },
    });

    autoTable(doc, {
      ...common,
      startY: doc.lastAutoTable.finalY + 3,
      body: DECLARATION.map((line) => [{ content: line, styles: { fontSize: 8, fontStyle: "normal" } }]),
    });

    autoTable(doc, {
      ...common,
      startY: doc.lastAutoTable.finalY + 3,
      body: [
        [labelCell("Received by Operative Name:"), val(record.receivedByOperativeName)],
        [labelCell("Signature:"), { content: "", styles: { minCellHeight: 20 } }],
        [labelCell("Date:"), val(toDdMmYyyy(record.receivedByDate))],
      ],
      columnStyles: { 0: { cellWidth: 60 } },
      didDrawCell: (d) => {
        if (d.section === "body" && d.row.index === 1 && d.column.index === 1) {
          const src = normalizeImageSource(record.receivedBySignature, "image/png");
          if (src) {
            try {
              const pr = doc.getImageProperties(src);
              const ratio = Math.min((d.cell.width - 4) / pr.width, (d.cell.height - 3) / pr.height);
              doc.addImage(src, getPdfImageType(src), d.cell.x + (d.cell.width - pr.width * ratio) / 2, d.cell.y + (d.cell.height - pr.height * ratio) / 2, pr.width * ratio, pr.height * ratio, undefined, "NONE");
            } catch {}
          }
        }
      },
    });

    const totalPagesPdf = doc.getNumberOfPages();
    for (let pg = 1; pg <= totalPagesPdf; pg += 1) {
      doc.setPage(pg);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${pg} of ${totalPagesPdf}`, pageWidth - 30, pageHeight - 8);
    }

    const safeName = String(record.operativeName || record.projectLocation || "plant-issue-form").replace(/[\\/:"*?<>|]+/g, "-");
    const shouldUpload = options.uploadOnly === true;
    const fileName = shouldUpload
      ? buildSharePointFileName([
          record.operativeName || addedByName(record),
          record.projectLocation,
          toDdMmYyyy(record.dateOfIssue),
        ])
      : `SF004G_${safeName}.pdf`;

    if (shouldUpload) {
      try {
        setUploadingKey(record._id || "");
        const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
        const dataUri = doc.output("datauristring");
        const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: JSON.stringify({ fileName, fileBase64: dataUri, uploadContext: "hseq-forms-tablets", uploadSubFolder: "SF-004-G Plant Issue Form" }),
        });
        if (res.status !== 204 && !res.ok) {
          let msg = "SharePoint upload failed";
          try { const out = await res.json(); msg = out?.message || msg; } catch {}
          throw new Error(msg);
        }
        toast.success("PDF uploaded to SharePoint successfully!");
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
      } finally {
        setUploadingKey(null);
      }
    } else {
      doc.save(fileName);
      toast.success("SF-004-G Plant Issue Form PDF generated!");
    }
  };

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-004-G Plant Issue Form</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button type="button" onClick={() => setShowImportModal(true)} style={{ background: THEME, color: "#fff", border: "none", borderRadius: 10, padding: "10px 14px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                Import File
              </button>
            )}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250, boxSizing: "border-box" }}
              />
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Operative Name</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Project/Location</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Date of Issue</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Plant Items</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}><i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />Loading records...</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>No records found.</td></tr>
              ) : (
                paged.map((r, idx) => {
                  const filledRows = (Array.isArray(r.plantRows) ? r.plantRows : []).filter(
                    (row) => `${row?.plantTtslIdNo || ""}${row?.descriptionOfPlant || ""}`.trim() !== ""
                  ).length;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.operativeName || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.projectLocation || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(r.dateOfIssue)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{filledRows || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(r.updatedAt || r.createdAt)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button type="button" onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          )}
                          <button type="button" onClick={() => generatePDF(r)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                            <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                          </button>
                          {(() => {
                            const busy = uploadingKey === (r._id || "");
                            return (
                              <button
                                type="button"
                                onClick={() => { if (!busy) generatePDF(r, { uploadOnly: true }); }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6, opacity: busy ? 0.6 : 1 }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button type="button" onClick={() => handleDelete(r._id || r.id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME, cursor: page === 1 ? "not-allowed" : "pointer" }}>Prev</button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>{page} / {totalPages}</span>
          <button type="button" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME, cursor: page === totalPages ? "not-allowed" : "pointer" }}>Next</button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 1040, margin: "0 auto" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff" }}>
                <h5 className="modal-title">Edit SF-004-G Plant Issue Form</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", background: "#f8fafc", padding: 16 }}>

                <div style={{ border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
                  <div style={SECTION_BAR}>PLANT ISSUE DETAILS</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <tbody>
                      <tr>
                        <td style={{ ...GRID_LABEL, width: "32%" }}>Operative Name:</td>
                        <td style={GRID_FIELD}><input className="form-control form-control-sm" value={editForm.operativeName || ""} onChange={(e) => setEditForm({ ...editForm, operativeName: e.target.value })} /></td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Project/Location:</td>
                        <td style={GRID_FIELD}><input className="form-control form-control-sm" value={editForm.projectLocation || ""} onChange={(e) => setEditForm({ ...editForm, projectLocation: e.target.value })} /></td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Date of Issue:</td>
                        <td style={GRID_FIELD}><DatePicker selected={parseStoredDate(editForm.dateOfIssue)} onChange={(d) => setEditForm({ ...editForm, dateOfIssue: d ? dateToYmd(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" placeholderText="Select date" /></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={{ border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
                  <div style={SECTION_BAR}>PLANT ISSUED</div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: 760 }}>
                      <thead>
                        <tr>
                          <th style={{ ...GRID_LABEL, background: THEME, color: "#fff", width: "20%" }}>Plant TTSL ID No.</th>
                          <th style={{ ...GRID_LABEL, background: THEME, color: "#fff", width: "32%" }}>Description of Plant</th>
                          <th style={{ ...GRID_LABEL, background: THEME, color: "#fff" }}>Comments:</th>
                          <th style={{ ...GRID_LABEL, background: THEME, color: "#fff", width: "20%" }}>Re-test Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ensureRows(editForm.plantRows).map((row, i) => (
                          <tr key={i}>
                            <td style={GRID_FIELD}><input className="form-control form-control-sm" value={row.plantTtslIdNo} onChange={(e) => updateRow(i, "plantTtslIdNo", e.target.value)} /></td>
                            <td style={GRID_FIELD}><input className="form-control form-control-sm" value={row.descriptionOfPlant} onChange={(e) => updateRow(i, "descriptionOfPlant", e.target.value)} /></td>
                            <td style={GRID_FIELD}><input className="form-control form-control-sm" value={row.comments} onChange={(e) => updateRow(i, "comments", e.target.value)} /></td>
                            <td style={GRID_FIELD}><DatePicker selected={parseStoredDate(row.retestDate)} onChange={(d) => updateRow(i, "retestDate", d ? dateToYmd(d) : "")} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" placeholderText="Select date" /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div style={{ border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
                  <div style={SECTION_BAR}>RECEIPT &amp; DECLARATION</div>
                  <div style={{ padding: "10px 12px", background: "#fff" }}>
                    {DECLARATION.map((line, i) => (
                      <p key={i} style={{ fontSize: 11, color: "#475569", marginBottom: 6 }}>{line}</p>
                    ))}
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <tbody>
                      <tr>
                        <td style={{ ...GRID_LABEL, width: "32%" }}>Received by Operative Name:</td>
                        <td style={GRID_FIELD}><input className="form-control form-control-sm" value={editForm.receivedByOperativeName || ""} onChange={(e) => setEditForm({ ...editForm, receivedByOperativeName: e.target.value })} /></td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Signature:</td>
                        <td style={GRID_FIELD}>{renderSignatureBox(editForm.receivedBySignature)}</td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Date:</td>
                        <td style={GRID_FIELD}><DatePicker selected={parseStoredDate(editForm.receivedByDate)} onChange={(d) => setEditForm({ ...editForm, receivedByDate: d ? dateToYmd(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" placeholderText="Select date" /></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600 }} onClick={handleEditSave}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-004-G Plant Issue Form File"
        importPath="sf-004-g-plant-issue-form/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setRecords((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-004-G Plant Issue Form</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this record?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
